import instantsearch from 'instantsearch.js';
import { searchBox, hits, configure } from 'instantsearch.js/es/widgets';
import searchClient from './searchClient';
import { KEY_CODES } from '../util';

const containers = {
  searchWrapper: '#nav-search-wrapper',
  hitsContainer: '#nav-search-hits-container',
  searchbox: '#nav-search-searchbox',
  hits: '#nav-search-hits',
  seeAllLink: '#nav-search-see-all',
};

class AlgoliaNavSearch {
  constructor(indexName) {
    this.indexName = indexName;
    this.blogId = parseInt(document.body.dataset.blogId, 10);
    this.blogName = document.body.dataset.blogName;
    this.blogUrl = document.body.dataset.blogUrl;
    this.searchWrapper = document.querySelector(containers.searchWrapper);
    this.seeAllLink = document.querySelector(containers.seeAllLink);
    this.hitsContainer = document.querySelector(containers.hitsContainer);
    this.searchInputClassName = 'nav__search-input';
    this.firstLoad = true;
    this.focus = 0;

    try {
      this.search = instantsearch({
        indexName: this.indexName,
        searchClient,
        searchFunction: helper => {
          // If this is the first load, do not perform a search
          if (this.firstLoad) {
            this.firstLoad = false;
            return;
          }

          const { query } = helper.state;
          // Hide the hits container if there is no query
          this.hitsContainer.classList.toggle('hide', !query);
          // Add query param to "See all results" link
          this.seeAllLink.href = `${this.blogUrl}/search?${
            this.indexName
          }&query=${encodeURIComponent(query)}`;

          helper.search();
        },
      });

      this.initialize();
      this.bindEvents();
    } catch (e) {
      console.error(`📣: AlgoliaNavSearch -> constructor -> e`, e);
    }
  }

  get isMainSite() {
    return this.blogId === 1;
  }

  get networkRankingFilters() {
    return [
      `blog_id:${this.blogId}<score=3>`,
      'is_front_page:true<score=2>',
      'blog_id:1<score=1>',
    ];
  }

  initialize() {
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/search-box/js/
      searchBox({
        container: containers.searchbox,
        placeholder: `Search ${this.isMainSite ? 'CSSH' : this.blogName}`,
        cssClasses: {
          input: this.searchInputClassName,
        },
        templates: {
          reset: `
            <svg class="search__reset">
              <use xlink:href="#icon-x"></use>
            </svg>
          `,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      configure({
        hitsPerPage: 5,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Search the whole multisite network, while ranking current-site records first
        optionalFilters: this.networkRankingFilters,
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: containers.hits,
        escapeHTML: false,
        templates: {
          item(hit) {
            return `
              <article>
                <a href="${hit.url}">
                  <span class="hit-title">
                    <strong>
                      ${instantsearch.highlight({ attribute: 'title', hit })}
                    </strong>
                  </span>
                  <span class="hit-type">
                    ${hit.is_front_page ? 'Department or School' : hit.type}
                  </span>
                </a>
              </article>
            `;
          },
          empty: `No results for <em>{{ query }}</em>`,
        },
        transformItems(items) {
          // Hide see all link if there are no results
          const seeAllLink = document.querySelector(containers.seeAllLink);
          seeAllLink.classList.toggle('hide', items.length < 1);

          // Change 'Person' type label to something more encompassing
          const transformed = items.map(item => ({
            ...item,
            type: item.type === 'person' ? 'Staff' : item.type,
          }));

          return transformed;
        },
      }),
    ]);

    this.search.start();
    this.setupSearchInput();
  }

  setupSearchInput() {
    // Give search input a label for accessibility
    this.searchInput = document.querySelector(`.${this.searchInputClassName}`);
    this.searchInput.setAttribute(
      'aria-label',
      `Search ${this.isMainSite ? 'CSSH' : this.blogName}`,
    );
  }

  bindEvents() {
    this.searchInput.addEventListener('keydown', e => this.onSearchKeydown(e));
    document.addEventListener('click', e => this.onDocumentClick(e));
    document.addEventListener('keydown', e => this.onDocumentKeydown(e));
  }

  onSearchKeydown(e) {
    // If enter is hit, go to the search page
    if (e.key === KEY_CODES.ENTER) {
      const query = e.target.value;
      const destinationUrl = `${this.blogUrl}/search?${this.indexName}&query=${encodeURIComponent(
        query,
      )}`;
      window.location.replace(destinationUrl);
    }
  }

  onDocumentKeydown(e) {
    // Bail early if the hits container is not open
    if (Array.from(this.hitsContainer.classList).includes('hide')) {
      return;
    }

    const hitLinks = document.querySelectorAll(`${containers.hits} .ais-Hits-item a`);

    // Bail early if there are no hits
    if (!hitLinks.length) {
      return;
    }

    this.focusableEls = [this.searchInput, ...Array.from(hitLinks), this.seeAllLink];
    this.lastFocusableEl = this.focusableEls[this.focusableEls.length - 1];

    // Use arrow keys to scroll through hits
    switch (e.key) {
      case KEY_CODES.ESCAPE:
      case KEY_CODES.ESCAPE_IE11:
        this.hitsContainer.classList.add('hide');
        break;

      case KEY_CODES.TAB:
        // Hide hits if tabbing out of search
        if (document.activeElement === this.lastFocusableEl) {
          this.hitsContainer.classList.add('hide');
        }
        // Return instead of break to allow tabbing to work normally
        return;

      case KEY_CODES.ARROW_DOWN:
      case KEY_CODES.ARROW_DOWN_IE11:
        e.preventDefault();

        // If focused on the last element, focus on the search input
        if (this.focus === this.focusableEls.length - 1) {
          this.focus = 0;
          break;
        }

        this.focus++;
        break;

      case KEY_CODES.ARROW_UP:
      case KEY_CODES.ARROW_UP_IE11:
        e.preventDefault();

        // If focused is on search input, focus on the last element
        if (this.focus === 0) {
          this.focus = this.focusableEls.length - 1;
          break;
        }

        this.focus--;
        break;

      default:
        return;
    }

    if (this.focusableEls[this.focus]) {
      this.focusableEls[this.focus].focus();
    } else {
      console.warn('No element to focus');
    }
  }

  // Hide nav search hits when clicking off
  onDocumentClick(e) {
    // Bail early if the nav hits are already hidden
    if (Array.from(this.hitsContainer.classList).includes('hide')) {
      return;
    }

    const wrapperContainsTarget = this.searchWrapper.contains(e.target);

    // If the click target is the search wrapper or an element inside the wrapper, hide hits
    if (e.target !== this.searchWrapper && !wrapperContainsTarget) {
      this.hitsContainer.classList.add('hide');
    }
  }

  static init() {
    new AlgoliaNavSearch();
  }
}

export default AlgoliaNavSearch;
